import React, { useEffect, useRef } from 'react';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Button from 'saur_ui_library/src/Atoms/Input/Button/Button';
import Form from 'saur_ui_library/src/Atoms/Input/Form/Form';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import Field from 'saur_ui_library/src/Molecules/Field/Field';
import { AdministrationApi } from '../../../utils/api';
import { Toast } from "primereact/toast";
import { sanitizeInput, isAnEmail } from '../../../utils/helpers/administration/addUserHelper';
import { loaderDataComponent } from '../../../utils/helper';
import { createGlobalStyle } from 'styled-components';

interface Props {
    view: string;
    getUsersUpdated?: () => void;
    row?: any;
}

const PopupUser = ({ view, getUsersUpdated, row }: Props) => {
    const [form, setForm] = React.useState<any>({
        lastname: '',
        firstname: '',
        email: '',
        role: 'user',
        roletype: 'consult',
    });
    const toastSuccess = useRef<any>(null);
    const toastError = useRef<any>(null);
    const [loading, setLoading] = React.useState<boolean>(false);

    const submitForm = async (pForm: any) => {
        setLoading(true);
        let body: any = {
            "login": pForm.email,
            "firstName": pForm.firstname,
            "lastName": pForm.lastname,
            "userRole": pForm.role,
            "roleType": pForm.roletype,
        };
        if (view === 'modify') {
            delete body.lastName;
            delete body.firstName;
        }
        try {
            const response = view === 'add' ? await AdministrationApi.addUser(body) : await AdministrationApi.updateUser(body);
            if (response.status === 200) {
                if(response.data === "UserAlreadyExists") {
                    toastError.current?.show({
                        severity: "error",
                        summary: "Erreur",
                        detail: "L'utilisateur existe déjà.",
                        life: 5000,
                    });
                } else {
                    toastSuccess.current?.show({
                        severity: "success",
                        summary: "Succès",
                        detail: view === 'add' ? "Utilisateur ajouté avec succès." : "Utilisateur modifié avec succès.",
                        life: 5000,
                    });
                }
                getUsersUpdated && getUsersUpdated();
            }
        } catch (error) {
            toastError.current?.show({
                severity: "error",
                summary: "Erreur",
                detail: view === 'add' ? "Échec de l'ajout de l'utilisateur." : "Échec de la modification de l'utilisateur.",
                life: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const sendMail = async () => {
        setLoading(true);
        try {
            const response = await AdministrationApi.sendMailToUser(row.email);
            if (response.status === 200) {
                toastSuccess.current?.show({
                    severity: "success",
                    summary: "Succès",
                    detail: "Email de confirmation envoyé avec succès.",
                    life: 5000,
                });
            }
        } catch (error) {
            toastError.current?.show({
                severity: "error",
                summary: "Erreur",
                detail: "Échec de l'envoi de l'email de confirmation.",
                life: 5000,
            });
        } finally {
            setLoading(false);
        }
    }

    const desactiveUser = async () => {
        setLoading(true);
        try {
            const response = await AdministrationApi.desactiveUser(row.email);
            if (response.status === 200) {
                toastSuccess.current?.show({
                    severity: "success",
                    summary: "Succès",
                    detail: "Utilisateur désactivé avec succès.",
                    life: 5000,
                });
                getUsersUpdated && getUsersUpdated();
            }
        } catch (error) {
            toastError.current?.show({
                severity: "error",
                summary: "Erreur",
                detail: "Échec de la désactivation de l'utilisateur.",
                life: 5000,
            });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (view === 'modify') {
            setForm({
                lastname: row.lastName || '',
                firstname: row.firstName || '',
                email: row.email || '',
                role: row.roleForEdition || '',
                roletype: row.roleType || '',
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Toast id="toast-success" ref={toastSuccess} />
            <Toast id="toast-error" ref={toastError} />
            <Styles />
            <Container
                style={{ width: '100%', height: '100%' }}
            >
            <Form
                onSubmit={() => { 
                    const isValidEmail = isAnEmail(form.email);
                    if (isValidEmail) {
                        submitForm(form);
                    } else {
                        toastError.current?.show({
                            severity: "error",
                            summary: "Erreur",
                            detail: "L'email saisi n'est pas valide.",
                            life: 5000,
                        });
                    }
                }}
            >
                <Word
                    value='Ajouter un utilisateur'
                    variant='span'
                    automatedTestId='admin-add-user-title'
                    textStyle='titleSmall'
                    className='admin-add-user-title'
                />
                <Field
                    label='Nom'
                    value={form.lastname || ''}
                    errorMessage=''
                    onChange={(v) => {
                        setForm((oldValues) => {
                        return { ...oldValues, lastname: sanitizeInput(v, 'strict') };
                        });
                    }}
                    disabled={view === 'modify' ? true : false}
                />
                <Field
                    label='Prénom'
                    value={form.firstname || ''}
                    errorMessage=''
                    onChange={(v) => {
                        setForm((oldValues) => {
                        return { ...oldValues, firstname: sanitizeInput(v, 'strict') };
                        });
                    }}
                    disabled={view === 'modify' ? true : false}
                />
                <Field
                    label='Email'
                    value={form.email || ''}
                    errorMessage=''
                    onChange={(v) => {
                        setForm((oldValues) => {
                        return { ...oldValues, email: sanitizeInput(v, 'email') };
                        });
                    }}
                    disabled={view === 'modify' ? true : false}
                />
                {form.email.includes('@saur') && <Word
                    value="Ne pas utiliser l'adresse email Saur pour un accès externe !"
                    variant='span'
                    automatedTestId='admin-add-user-email'
                    textStyle='tinyRegular'
                    className='warningEmail'
                />}
                <br />
                {view === 'modify' && <div style={{ display: 'flex', width: '100%', justifyContent: 'space-evenly' }}>
                    <Button type='button' onClick={desactiveUser} className='buttonDesactiveUser' disabled={row.enabled === false ? true : false}>
                        <Word
                            value='Désactiver le compte'
                            variant='span'
                            automatedTestId='admin-add-user'
                            textStyle='normalReg'
                        />
                    </Button>
                    <Button type='button' onClick={sendMail} className='buttonSendMail'>
                        <Word
                            value='Renvoyer le mail de confirmation'
                            variant='span'
                            automatedTestId='admin-add-user'
                            textStyle='normalReg'
                        />
                    </Button>
                </div>}
                <br />
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', zIndex: 1 }}>
                    {loading === false ? (view === 'add' && <Button type='submit' disabled={!form.lastname || !form.firstname || !form.email || form.email.includes('@saur')} className='buttonSendMail'>
                        <Word
                            value={'Ajouter l’utilisateur'}
                            variant='span'
                            automatedTestId='admin-add-user'
                            textStyle='title'
                        />
                    </Button>) : loaderDataComponent(loading)}
                </div>
          </Form>
        </Container>
        </>
    )
}

const Styles = createGlobalStyle`
.sds-reset-style {
    .buttonSendMail, .buttonDesactiveUser {
        width: 40%;
    }
    .warningEmail{
        color: red;
    }
}
`;

export default PopupUser;